import { useLocation, useNavigate } from 'react-router-dom';
import { useContext, useMemo, useState } from 'react';
import { Company } from '../../services/types';
import CompaniesContext from '../../context/CompaniesContext';
import { useOrganizationContext } from '../../context/OrganizationContext';
import { formatDate } from '../../utils/helpers';

function MergeCompanies() {
    const navigate = useNavigate();
    const location = useLocation();
    const { companies, fetchCompanies } = useContext(CompaniesContext);
    const { partnerId } = useOrganizationContext();

    const [retainedCompanyId, setRetainedCompanyId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const selectedCompanyIds: number[] = location.state?.selectedCompanies || [];

    // Look up full company objects from IDs
    const selectedCompanies: Company[] = useMemo(
    () => selectedCompanyIds
        .map(id => companies.find(company => company.id === id))
        .filter((company): company is Company => !!company),
    [selectedCompanyIds, companies]
    );

    // filter out the retained company
    const sourceCompanyIds = selectedCompanies
        .filter(company => company.id !== retainedCompanyId)
        .map(company => company.id);

    console.log('partner id:', partnerId);
    console.log('companies to merge:', sourceCompanyIds);

    const handleConfirm = async () => {
    if (!retainedCompanyId) {
        setError('Please select a retained company to merge into.');
        return;
    }
    if (sourceCompanyIds.length === 0) {
        setError('Please select at least one company to merge.');
        return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
        // Create payload
        const payload = {
          retainedCompanyId,
          sourceCompanyIds
        }

          const response = await fetch(`${import.meta.env.VITE_COMMERCE_BACKEND_URL}/api/mergecompanies?partnerId=${partnerId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });

          const data = await response.json();
          console.log('data:', data);

          if (!response.ok) {
            throw new Error(data.error || 'Failed to merge companies');
          }

        await fetchCompanies(); // ✅ Refresh company list after merging
        // Navigate to a result page and pass success
        navigate('/companies/merge/result', { state: { success: true, message: 'The selected companies have been successfully merged into the retained company.' } });
    } catch (err: any) {
        navigate('/companies/merge/result', { state: { success: false, message: err.message || 'Merge failed.' } });
    }
  };

    const handleCancel = () => {
        navigate('/companies');
    };

    return (
    <div className="m-2 p-4">
        <div className="mb-8 border border-solid border-color-tableGray rounded-md">
            <div className="border-b border-solid border-titanium-white mb-5 bg-gotham-gray rounded-t-md">
                <h2 className="text-white font-semibold py-3 ml-7">Merge Companies</h2>
            </div>

            <div className="flex ml-8 my-3 items-center">
                <p className="font-semibold">Select the retained company. All other selected companies will be merged into this one:</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mx-8 mb-8">
            {selectedCompanies.map((company) => {
                const isRetained = company.id === retainedCompanyId;

                return (
                <div
                    key={company.id}
                    className={`group relative p-6 border rounded-lg shadow-sm cursor-pointer transition-all duration-200 hover:shadow-md ${
                    isRetained ? 'border-blue-600 bg-blue-50' : 'border-gray-200 bg-white'
                    }`}
                    onClick={() => setRetainedCompanyId(company.id)}
                >
                    {/* Retained badge */}
                    {isRetained && (
                    <div className="absolute top-3 right-3 text-xs font-medium text-blue-700 bg-blue-100 border border-blue-400 px-2 py-0.5 rounded">
                        Retained
                    </div>
                    )}

                    {/* Title */}
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    {company.legal_name || company.trade_name || '(Unnamed Company)'}
                    </h3>

                    {/* Details */}
                    <dl className="space-y-1 text-sm text-gray-700">
                    <div className="flex justify-between">
                        <dt className="font-medium">UBI:</dt>
                        <dd>{company.ubi || 'N/A'}</dd>
                    </div>
                    <div className="flex justify-between">
                        <dt className="font-medium">Trade Name:</dt>
                        <dd>{company.trade_name || 'N/A'}</dd>
                    </div>
                    <div className="flex justify-between">
                        <dt className="font-medium">Legal Name:</dt>
                        <dd>{company.legal_name || 'N/A'}</dd>
                    </div>
                    <div className="flex justify-between">
                        <dt className="font-medium">Address:</dt>
                        <dd>{company.address || 'N/A'}</dd>
                    </div>
                    <div className="flex justify-between">
                        <dt className="font-medium">Interactions:</dt>
                        <dd>{company.total_interactions}</dd>
                    </div>
                    <div className="flex justify-between">
                        <dt className="font-medium">Last Interaction:</dt>
                        <dd>{formatDate(company.last_interaction_date)}</dd>
                    </div>
                    </dl>
                </div>
                );
            })}
            </div>

            {error && (
                <div className="text-red-500 text-sm ml-8 mb-2">
                {error}
                </div>
            )}

            <div className="my-4 mx-8 flex space-x-4">
                <button
                    onClick={handleConfirm}
                    disabled={isSubmitting}
                    className="w-36 h-9 text-center text-white bg-gotham-gray border border-solid border-gotham-gray rounded-md focus:outline-none focus:border-blue-400 hover:bg-blue-400 hover:border-blue-500 disabled:opacity-50"
                >
                Confirm
                </button>

                <button
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    className="w-36 h-9 text-center text-gotham-gray bg-white border border-solid border-gotham-gray rounded-md focus:outline-none focus:border-red-500 hover:bg-red-400 hover:border-red-500 hover:text-white"
                >
                Cancel
                </button>
            </div>
        </div>
    </div>
    );
}

export default MergeCompanies;
